import { z } from "zod";

import { VIBE_SESSION_RENEWAL_PATH } from "./vibe-session-renewal.schema";

/**
 * The sign-in code — the only credential the Vibe app sign-in puts in a URL.
 *
 * ## Why a code, and not the assertion
 *
 * The console used to hand the browser `/.vibe/callback?assertion=<JWS>`. A URL
 * is written to browser history, to a `Referer`, and to every access log between
 * the browser and the edge — Cloudflare, the ALB and Traefik's own, which ships
 * to a 90-day bucket. Since sessions renew up to their absolute cap, a leaked
 * assertion was a working credential for up to twelve hours.
 *
 * A code is the OAuth authorization-code answer to the same constraint: it is
 * worthless on its own, the edge exchanges it SERVER-TO-SERVER for the assertion,
 * it can be exchanged ONCE, it lives {@link VIBE_SESSION_CODE_TTL_SECONDS}, and it
 * is bound to the app and host it was minted for. Whatever a log keeps is spent
 * by the time anyone reads it.
 *
 * ## Why not the two alternatives
 *
 * - A `form_post` body is not available here: Traefik's ForwardAuth forwards a
 *   request's method, URI and headers to the authorizer, never its body, and
 *   turning body forwarding on is per MIDDLEWARE — it would buffer every request
 *   to the app, uploads included, to read one sign-in.
 * - A URL fragment never reaches a server but still reaches history, and needs
 *   script on a response the edge writes for someone else's app.
 *
 * ## Ceiling
 *
 * debt: the code is not bound to the BROWSER (no PKCE / state cookie), so a code
 *       read from a log and redeemed before the browser's own callback — a window
 *       of about a second, since the browser follows the redirect at once — gets
 *       the session, and the real sign-in then fails as already redeemed.
 *       Upgrade trigger: any `already-redeemed` exchange outcome in the backend
 *       log (`ExchangeVibeSessionCodeUseCase` warns on it), which is exactly the
 *       shape that race leaves behind.
 */

/**
 * Query parameter on `/.vibe/callback` carrying the one-time code.
 *
 * `code` because that is what OAuth calls it and what every log reader already
 * knows to treat as spent-on-use.
 */
export const VIBE_CALLBACK_CODE_PARAM = "code";

/**
 * Query parameter the EDGE adds to the sign-in URL to say it can redeem a code.
 *
 * This is how an old router and a new control plane — or the reverse — still
 * sign people in during a rollout: the control plane mints a code only for a
 * router that asked for one, and a router asks only when it holds an exchange
 * URL. The name and value are OAuth's `response_type=code`.
 */
export const VIBE_AUTHORIZE_RESPONSE_TYPE_PARAM = "response_type";

/** The one value of {@link VIBE_AUTHORIZE_RESPONSE_TYPE_PARAM} anything sends. */
export const VIBE_AUTHORIZE_RESPONSE_TYPE_CODE = "code";

/**
 * How long a minted code can be exchanged, in seconds.
 *
 * The browser follows the console's redirect immediately, so a real sign-in
 * redeems its code within a second or two; sixty is headroom for a slow network,
 * not a feature. The code's store enforces it — the value dies in Redis — so no
 * clock on either side has to agree.
 */
export const VIBE_SESSION_CODE_TTL_SECONDS = 60;

/** Path on the control plane where a tenant's edge redeems a sign-in code. */
export const VIBE_SESSION_EXCHANGE_PATH = "/api/vibe/edge/session-exchanges";

/**
 * The exchange endpoint, derived from the renewal endpoint the edge already
 * holds — or `null` when it holds none.
 *
 * Derived rather than published separately because a new field in the edge-authz
 * state document would have to cross the tenant agent too, and a router whose
 * agent predates it would then never learn the URL. Both routes sit on the same
 * control-plane base, so the renewal URL IS the publication; this swaps the one
 * path for the other and refuses any URL that does not end in the renewal path,
 * rather than guessing at a base.
 */
export function vibeSessionExchangeUrlFor(renewalUrl: string | null): string | null {
  if (renewalUrl === null || !renewalUrl.endsWith(VIBE_SESSION_RENEWAL_PATH)) return null;

  return `${renewalUrl.slice(0, -VIBE_SESSION_RENEWAL_PATH.length)}${VIBE_SESSION_EXCHANGE_PATH}`;
}

/**
 * What the edge sends to redeem a code.
 *
 * `authorizationCode`, not `code`, on purpose: this body is what an error report
 * would capture, and the shared credential vocabulary redacts a key carrying
 * `authorization` where a bare `code` — a country code, an error code — is
 * rightly left readable.
 */
export const VibeSessionExchangeRequestSchema = z.object({
  authorizationCode: z.string().min(16).max(128),
  /** The app the router's ForwardAuth middleware guards — from its path. */
  appId: z.string().min(1).max(128),
  /** The host the callback arrived on; the code is bound to it. */
  host: z.string().min(1).max(253)
});

export type VibeSessionExchangeRequest = z.infer<typeof VibeSessionExchangeRequestSchema>;

/** The assertion the code stood for. The edge verifies it before it becomes a cookie. */
export const VibeSessionExchangeResponseSchema = z.object({
  assertion: z.string().min(1)
});

export type VibeSessionExchangeResponse = z.infer<typeof VibeSessionExchangeResponseSchema>;
